export interface Permission {
  id: number;
  name: string | null;
  permissionId?: number | string | null;
  displayName?: string | null;
  permissionName?: string | null;
  description?: string | null;
  code?: string | null;
  value?: string | null;
  key?: string | null;
  resource?: string | null;
  action?: string | null;
}

export type PermissionApiResponseItem = Permission | Record<string, unknown> | string | number;

export interface PermissionsResponseWrapper {
  permissions?: PermissionApiResponseItem[];
  data?: PermissionApiResponseItem[];
  items?: PermissionApiResponseItem[];
  results?: PermissionApiResponseItem[];
}

export type PermissionsResponse = PermissionApiResponseItem[] | PermissionsResponseWrapper;

export function normalizePermission(
  permission: PermissionApiResponseItem,
  fallbackIndex = 0,
): Permission {
  if (typeof permission === 'string') {
    return { id: fallbackIndex + 1, name: permission };
  }

  if (typeof permission === 'number') {
    return { id: permission, name: String(permission) };
  }

  const permissionRecord = permission as Record<string, unknown>;
  const id = getNumberField(permissionRecord, 'id') ||
    getNumberField(permissionRecord, 'permissionId') ||
    fallbackIndex + 1;
  const compoundName = [
    getStringField(permissionRecord, 'resource'),
    getStringField(permissionRecord, 'action'),
  ]
    .filter(Boolean)
    .join(' - ');
  const name =
    getStringField(permissionRecord, 'name') ||
    getStringField(permissionRecord, 'displayName') ||
    getStringField(permissionRecord, 'permissionName') ||
    getStringField(permissionRecord, 'description') ||
    getStringField(permissionRecord, 'code') ||
    getStringField(permissionRecord, 'value') ||
    getStringField(permissionRecord, 'key') ||
    compoundName ||
    `Permission ${id}`;

  return { ...permissionRecord, id, name } as Permission;
}

function getStringField(item: Record<string, unknown>, fieldName: string): string | null {
  const value = item[fieldName];

  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function getNumberField(item: Record<string, unknown>, fieldName: string): number | null {
  const value = item[fieldName];
  const numberValue = typeof value === 'number' ? value : Number(value);

  return Number.isNaN(numberValue) ? null : numberValue;
}